import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Milliseconds, UserAttributeKeyDatePrecision, isNumber } from '@iotv/datamodel-core';
import { isNonNaNNumber } from '@iotv/iotv-v3-types';
import { Cancel } from '@mui/icons-material';
import { Checkbox, IconButton, MenuItem, Select, TextField } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import dayjs from 'dayjs';
import { NumericField } from '../../../../../components/common/NumericField';
import { ActionIcons } from '../../../../../Iconography';
import { isInLimits, validByRegex } from './validation';
export const getEditCellRenderer = ({ validationMessageFn, viewKeyDefinition, }) => {
    const cellRendererMap = {
        string: (p) => _jsx(GridCellString, { gridRenderCellParams: p, viewKeyDefinition, validationMessageFn }),
        number: (p) => _jsx(GridCellNumber, { gridRenderCellParams: p, viewKeyDefinition, validationMessageFn }),
        boolean: (p) => _jsx(GridCellBooleanChecker, { gridRenderCellParams: p }),
        enum: (p) => _jsx(GridCellEnumSelector, { viewKeyDefinition, gridRenderCellParams: p, validationMessageFn }),
        date: (p) => _jsx(GridCellDate, { gridRenderCellParams: p, viewKeyDefinition, validationMessageFn }),
    };
    return viewKeyDefinition.type ? cellRendererMap[viewKeyDefinition.type] : () => _jsx("div", { children: "No viewKeyDefinition.type" });
};
const GridCellEnumSelector = ({ viewKeyDefinition, gridRenderCellParams, validationMessageFn }) => {
    'use no memo';
    // Error: Cannot access refs during render
    const debug = process.env.REACT_APP_DEBUG && false;
    const unselectedEntry = { k: 'Select a value', v: '' };
    const apiRef = useGridApiContext();
    const enumeration = viewKeyDefinition.enumKV ?? {};
    const enumKeyFromValue = (gridRenderCellParams.value?.v ?? '').toString();
    debug && console.log('GridCellEnumSelector View has ', { enumeration, enumKeyFromValue });
    return (_jsxs(Select, { labelId: `_label`,
        id: `_select`,
        displayEmpty: true,
        value: enumKeyFromValue,
        onChange: (event, child) => {
            event.stopPropagation();
            event.preventDefault();
            const value = event.target.value;
            const { field, id, value: originalValue } = gridRenderCellParams;
            let enumValue = undefined;
            switch (typeof value) {
                case 'string':
                    if (value.length)
                        enumValue = value;
                    break;
                case 'bigint':
                case 'number':
                case 'boolean':
                    enumValue = value;
                    break;
            }
            apiRef.current.setEditCellValue({ field, id, value: { k: originalValue?.k ?? '', v: enumValue } });
            if (viewKeyDefinition.required && !viewKeyDefinition.hidden) {
                if (enumValue !== undefined) {
                    validationMessageFn({ [viewKeyDefinition.key]: undefined });
                }
                else {
                    validationMessageFn({
                        [viewKeyDefinition.key]: { type: 'required', content: `${viewKeyDefinition.label} is required${debug ? ` ${value}` : ''}` },
                    });
                }
            }
        }, children: [_jsx(MenuItem, { value: unselectedEntry.v, children: _jsx("em", { children: unselectedEntry.k }) }), Object.entries(enumeration).map(([k, v], i) => (_jsx(MenuItem, { value: v, children: k }, k)))] }));
};
const GridCellBooleanChecker = ({ gridRenderCellParams }) => {
    'use no memo';
    // Error: Cannot access refs during render
    const apiRef = useGridApiContext();
    const { field, id, value: originalValue } = gridRenderCellParams;
    const handleValueChange = (event, checked) => {
        event.stopPropagation();
        event.preventDefault();
        apiRef.current.setEditCellValue({ field, id, value: { k: originalValue?.k ?? '', v: checked } });
    };
    return (_jsx(Checkbox, { autoFocus: true,
        checked: originalValue?.v === true,
        onChange: handleValueChange }));
};
const GridCellNumber = ({ gridRenderCellParams, viewKeyDefinition, validationMessageFn }) => {
    'use no memo';
    // Error: Cannot access refs during render
    const debug = process.env.REACT_APP_DEBUG && false;
    debug && console.log('GridCellNumber viewKeyDefinition', viewKeyDefinition);
    const { limits = {}, valueSetter, valueGetter, required, hidden } = viewKeyDefinition;
    const apiRef = useGridApiContext();
    const { field, id, value: originalValue } = gridRenderCellParams;
    const originalNumber = typeof originalValue?.v === 'number' ? originalValue.v : typeof originalValue?.v === 'string' ? parseFloat(originalValue.v) : undefined;
    const displayNumber = valueGetter ? valueGetter(originalNumber, undefined) : originalNumber;
    const handleNumberChange = (number) => {
        debug && console.log('GridCellNumber got change', number);
        let newValue = number;
        if (valueSetter) {
            newValue = valueSetter(newValue);
            debug && console.log('Used value setter on ', { originalNumber, eventVal: number, newValue });
        }
        apiRef.current.setEditCellValue({ field, id, value: { k: originalValue?.k ?? '', v: newValue } });
        if (isNumber(newValue)) {
            if (isInLimits(newValue, limits)) {
                debug && console.log('is in limits', { newValue, limits });
                validationMessageFn({ [viewKeyDefinition.key]: undefined });
            }
            else {
                const { min, max } = limits;
                validationMessageFn({
                    [viewKeyDefinition.key]: {
                        type: 'error',
                        content: `${viewKeyDefinition.label} has limits: ${isNonNaNNumber(min) ? `min: ${min}` : ''} ${isNonNaNNumber(max) ? `max: ${max}` : ''}`,
                    },
                });
            }
        }
        else if (required && !hidden) {
            validationMessageFn({ [viewKeyDefinition.key]: { type: 'required', content: `${viewKeyDefinition.label} is required${debug ? ` ${number}` : ''}` } });
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(NumericField, { value: displayNumber ?? undefined, onChange: handleNumberChange, fullWidth: true, autoFocus: true, slotProps: { input: limits } }), _jsx(IconButton, { onClick: () => {
                    const resetVal = limits ? limits.min : 0;
                    debug && console.log(`RESET ${originalValue.k} to ${resetVal}`);
                    apiRef.current.setEditCellValue({ field, id, value: { k: originalValue?.k ?? '', v: resetVal } });
                }, children: _jsx(ActionIcons.Clear, {}) })] }));
};
const GridCellString = ({ gridRenderCellParams, viewKeyDefinition, validationMessageFn }) => {
    'use no memo';
    // Error: Cannot access refs during render
    const debug = process.env.REACT_APP_DEBUG && false;
    const apiRef = useGridApiContext();
    const { field, id, value: originalValue } = gridRenderCellParams;
    const { validationRx, pass, failsValidationText, required, hidden, valueSetter, valueGetter } = viewKeyDefinition;
    debug && console.log('GridCellString validation', gridRenderCellParams, viewKeyDefinition);
    const displayValue = typeof originalValue?.v === 'string' ? originalValue.v : '';
    debug && console.log(`GridCellString ${id} EDIT has value`, { originalValue, displayValue });
    const handleValueChange = (event) => {
        event.stopPropagation();
        event.preventDefault();
        const value = event.target.value;
        debug && console.log(`GridCellString ${id} EDIT got change`, { originalValue, event });
        /** 2023 10 changed so that value is set even if validation fails, because user must be able to type partials */
        /** 2024 09 - valueSetter on text is not implemented, rectifying */
        let newValue = value;
        if (valueSetter) {
            newValue = valueSetter(newValue);
            debug && console.log('GridCellString used value setter (string) on ', { originalValue, eventVal: event.target.value, newValue });
        }
        apiRef.current.setEditCellValue({ field, id, value: { k: originalValue?.k ?? '???', v: newValue } });
        if (newValue?.length) {
            /** 2024 09, where we need pretty input eg hex numbers for appKey, validation should be done post valueFn() call */
            // const valueToValidate = viewKeyDefinition.valueSetter?.( value ) ?? value
            if (validByRegex(newValue, validationRx, pass)) {
                debug && console.log('GridCellString valid by regex', { oldValue: value, newValue, validationRx });
                validationMessageFn({ [viewKeyDefinition.key]: undefined });
            }
            else {
                debug && console.log('GridCellString NOT valid by regex', { oldValue: value, newValue, validationRx, pass });
                validationMessageFn({
                    [viewKeyDefinition.key]: { type: 'error', content: `${debug ? `[${viewKeyDefinition.label}] ` : ''}${failsValidationText}: ${event.target.value}` },
                });
            }
        }
        else if (required && !hidden) {
            validationMessageFn({
                [viewKeyDefinition.key]: { type: 'required', content: `${viewKeyDefinition.label} is required${debug ? ` ${event.target.value}` : ''}` },
            });
        }
    };
    /** dont use valueGetter as value because on each keystroke the field will rerender and move the edit cursor to the end */
    return (_jsx(TextField, { value: displayValue,
        autoFocus: true,
        type: 'string',
        onChange: handleValueChange,
        style: { width: '100%' } }));
};
const GridCellDate = ({ gridRenderCellParams, viewKeyDefinition }) => {
    'use no memo';
    // Error: Cannot access refs during render
    const debug = process.env.REACT_APP_DEBUG && false;
    const apiRef = useGridApiContext();
    const { precision, valueSetter, valueGetter } = viewKeyDefinition;
    const { field, id, value: originalValue } = gridRenderCellParams;
    const transformedNumber = valueGetter ? valueGetter(originalValue.v, undefined) : originalValue.v;
    const displayValue = isNonNaNNumber(transformedNumber) ? new Date(transformedNumber) : undefined;
    console.log(`GridCellDate ${id} EDIT has value`, { transformedNumber, originalValue, displayValue });
    /** TODO establish why this is left bypassed --- looks like when time picker was added (dayjs)*/
    const handleValueChange = (event) => {
        event.stopPropagation();
        event.preventDefault();
        const value = event.target.value;
        console.log(`GridCellDate ${id} EDIT got change`, { originalValue, event });
        apiRef.current.setEditCellValue({ field, id, value: { k: originalValue?.k ?? '', v: value } });
    };
    const handleChange = (newValue) => {
        debug && console.log('GridCellDate got original fn val', newValue);
        const newTimeMs = newValue ? newValue.unix() * Milliseconds.SECOND : undefined;
        const transfomedNumber = valueSetter ? valueSetter(newTimeMs) : newTimeMs;
        apiRef.current.setEditCellValue({ field, id, value: { k: originalValue?.k ?? '', v: transfomedNumber } });
    };
    const importFormatMap = {
        [UserAttributeKeyDatePrecision.Date]: 'DD MMM YYYY',
        [UserAttributeKeyDatePrecision.DateTimeMins]: 'DD MMM YYYY HH:mm',
        [UserAttributeKeyDatePrecision.DateTimeSecs]: 'DD MMM YYYY HH:mm:ss',
    };
    const displayDayjs = displayValue ? dayjs(displayValue) : null;
    const resolvedPrecision = precision ?? UserAttributeKeyDatePrecision.DateTimeMins;
    return (_jsxs(LocalizationProvider, { dateAdapter: AdapterDayjs, children: [[UserAttributeKeyDatePrecision.Date].includes(resolvedPrecision) ? (_jsx(MobileDatePicker, { label: `Set ${viewKeyDefinition.label}`,
                inputFormat: precision && importFormatMap[precision]
                    ? importFormatMap[precision]
                    : importFormatMap[UserAttributeKeyDatePrecision.Date],
                value: displayDayjs,
                onChange: handleChange,
                slotProps: {
                    actionBar: { actions: ['today'] },
                },
                renderInput: (params) => _jsx(TextField, { ...params }) })) : (_jsx(MobileDateTimePicker, { label: `Set ${viewKeyDefinition.label}`,
                inputFormat: precision && importFormatMap[precision]
                    ? importFormatMap[precision]
                    : importFormatMap[UserAttributeKeyDatePrecision.Date],
                value: displayDayjs,
                onChange: handleChange,
                slotProps: {
                    actionBar: { actions: ['today'] },
                } })), _jsx(IconButton, { onClick: () => {
                    apiRef.current.setEditCellValue({ field, id, value: { k: originalValue?.k ?? '', v: undefined } });
                }, children: _jsx(Cancel, {}) })] }));
};
